/**
 * Given a freshly typed input string, the caret position within it, and a
 * reformatted output string (e.g. with a "$" prefix and thousands separators),
 * return the caret position in the reformatted string that preserves the
 * user's logical position relative to the digits they typed.
 *
 * Strategy: count digit characters strictly before the caret in the typed
 * string; walk the formatted string and place the caret immediately after the
 * same number of digit characters. Non-digit characters (the "$", commas,
 * a leading "-") are skipped in the count but pass through naturally.
 */
export function computeCaretAfterReformat(
  typed: string,
  caretIndex: number,
  formatted: string,
): number {
  if (formatted.length === 0) return 0;

  const clampedCaret = Math.max(0, Math.min(caretIndex, typed.length));
  let digitsBeforeCaret = 0;
  for (let i = 0; i < clampedCaret; i++) {
    if (typed[i] >= '0' && typed[i] <= '9') {
      digitsBeforeCaret++;
    }
  }

  if (digitsBeforeCaret === 0) {
    // Place caret after any leading non-digit prefix (e.g., after "$" or "-$").
    let i = 0;
    while (i < formatted.length && (formatted[i] < '0' || formatted[i] > '9')) {
      i++;
    }
    return i;
  }

  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (formatted[i] >= '0' && formatted[i] <= '9') {
      seen++;
      if (seen === digitsBeforeCaret) {
        return i + 1;
      }
    }
  }

  return formatted.length;
}
